import { useMemo, useState } from 'react'
import { ArrowRight, KeyRound, Network, TableProperties } from 'lucide-react'
import { Link } from 'react-router-dom'
import { coreDataModelTables, dataModelDomains, dataModelRelationships, dataModelTables, type DataModelDomain, type DataModelTable } from '../data/dataModel'
import { useI18n } from '../i18n'

const domainOrder: DataModelDomain[] = ['commerce', 'fulfillment', 'supply', 'marketing', 'finance']

const relationshipPaths = [
  ['c1_customers', 'c1_orders', 'c1_order_items', 'c1_products'],
  ['c1_orders', 'c1_order_fulfillment', 'c1_shipments_latest'],
  ['c1_order_items', 'c1_returns', 'c1_settled_refunds'],
  ['c1_marketing_campaigns', 'c1_customer_acquisitions', 'c1_customers'],
  ['c1_products', 'c1_inventory_daily'],
]

function tableLabel(table: DataModelTable, locale: 'en' | 'zh') {
  return locale === 'zh' ? table.labelZh : table.label
}

function RelationshipPath({ ids }: { ids: string[] }) {
  const { locale } = useI18n()
  return <div className="schema-relation-path">{ids.map((id, index) => {
    const table = dataModelTables.find((item) => item.id === id)!
    return <span key={id}><b>{tableLabel(table, locale)}</b><small>{id.replace('c1_', '')}</small>{index < ids.length - 1 && <ArrowRight size={15} aria-hidden="true" />}</span>
  })}</div>
}

export function DataModelPreview() {
  const { copy, path } = useI18n()
  const c = copy.landing.dataModel
  return <div className="data-model-preview" aria-labelledby="landing-data-model-title">
    <div className="data-model-preview-copy">
      <span>{c.eyebrow}</span>
      <h3 id="landing-data-model-title">{c.title}</h3>
      <p>{c.copy}</p>
      <Link to={`${path('/analytics/metrics')}#data-model`}>{c.action}<ArrowRight size={15} /></Link>
    </div>
    <div className="data-model-preview-map" aria-label={c.relationshipLabel}>
      {relationshipPaths.slice(0, 3).map((ids) => <RelationshipPath key={ids.join('-')} ids={ids} />)}
    </div>
  </div>
}

export function DataModelExplorer() {
  const { copy, locale } = useI18n()
  const c = copy.metrics.dataModel
  const [mode, setMode] = useState<'core' | 'full'>('core')
  const visibleTables = mode === 'core' ? coreDataModelTables : dataModelTables
  const visibleIds = useMemo(() => new Set(visibleTables.map((table) => table.id)), [visibleTables])
  const visibleRelationships = dataModelRelationships.filter((relationship) => visibleIds.has(relationship.from) && visibleIds.has(relationship.to))

  return <section id="data-model" className="workspace-panel data-model-panel" aria-labelledby="data-model-title">
    <div className="workspace-panel-header data-model-header">
      <div><span>{c.eyebrow}</span><h2 id="data-model-title">{c.title}</h2><p>{c.copy}</p></div>
      <div className="schema-mode-toggle" role="group" aria-label={c.modeLabel}>
        <button type="button" className={mode === 'core' ? 'active' : ''} aria-pressed={mode === 'core'} onClick={() => setMode('core')}>{c.core}</button>
        <button type="button" className={mode === 'full' ? 'active' : ''} aria-pressed={mode === 'full'} onClick={() => setMode('full')}>{c.full}</button>
      </div>
    </div>

    <div className="schema-summary">
      <span><TableProperties size={15}/><b>{visibleTables.length}</b> {c.tables}</span>
      <span><Network size={15}/><b>{visibleRelationships.length}</b> {c.relationships}</span>
      <p>{mode === 'core' ? c.coreContext : c.fullContext}</p>
    </div>

    {mode === 'core' && <div className="schema-paths" aria-label={c.relationshipLabel}>
      {relationshipPaths.map((ids) => <RelationshipPath key={ids.join('-')} ids={ids} />)}
    </div>}

    <div className={`schema-domain-grid ${mode}`}>
      {domainOrder.map((domain) => {
        const tables = visibleTables.filter((table) => table.domain === domain)
        if (!tables.length) return null
        const domainCopy = dataModelDomains[domain]
        return <section className={`schema-domain ${domain}`} key={domain} aria-labelledby={`schema-${domain}`}>
          <div className="schema-domain-title"><span /><h3 id={`schema-${domain}`}>{locale === 'zh' ? domainCopy.labelZh : domainCopy.label}</h3><b>{tables.length}</b></div>
          <div className="schema-table-list">{tables.map((table) => <SchemaTableCard key={table.id} table={table} visibleIds={visibleIds} />)}</div>
        </section>
      })}
    </div>
    <div className="schema-contract-note"><KeyRound size={15}/><p>{c.contractNote}</p></div>
  </section>
}

function SchemaTableCard({ table, visibleIds }: { table: DataModelTable; visibleIds: Set<string> }) {
  const { copy, locale } = useI18n()
  const c = copy.metrics.dataModel
  const relations = dataModelRelationships.filter((relationship) =>
    visibleIds.has(relationship.from) && visibleIds.has(relationship.to) && (relationship.from === table.id || relationship.to === table.id),
  )
  return <article className="schema-table-card">
    <header><TableProperties size={15}/><div><strong>{tableLabel(table, locale)}</strong><code>{table.id}</code></div></header>
    <p>{locale === 'zh' ? table.grainZh : table.grain}</p>
    <div className="schema-fields"><span><b>PK</b>{table.primaryKey.join(' + ')}</span>{table.fields.map((field) => <span key={field}><i />{field}</span>)}</div>
    <div className="schema-links" aria-label={c.tableRelationships}>{relations.map((relationship) => {
      const isParent = relationship.from === table.id
      const otherId = isParent ? relationship.to : relationship.from
      const other = dataModelTables.find((item) => item.id === otherId)!
      return <span key={`${relationship.from}-${relationship.to}`} title={relationship.key}>{isParent ? '→' : '←'} {tableLabel(other, locale)} <b>{relationship.cardinality}</b></span>
    })}</div>
  </article>
}

